import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

/* ── Inline Feather-style SVG icons (24x24 viewBox, 2px stroke) ────
   Inlined so we ship no icon library and avoid an extra request. */
const Icon = ({ d, size = 18, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {d}
  </svg>
);

const IconHome      = (p) => <Icon {...p} d={<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>} />;
const IconFileText  = (p) => <Icon {...p} d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></>} />;
const IconSliders   = (p) => <Icon {...p} d={<><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></>} />;
const IconUser      = (p) => <Icon {...p} d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>} />;
const IconFolder    = (p) => <Icon {...p} d={<><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></>} />;
const IconLogOut    = (p) => <Icon {...p} d={<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>} />;
const IconSun       = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></>} />;
const IconMoon      = (p) => <Icon {...p} d={<><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></>} />;
const IconMenu      = (p) => <Icon {...p} d={<><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" /></>} />;
const IconX         = (p) => <Icon {...p} d={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} />;
const IconExternal  = (p) => <Icon {...p} d={<><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></>} />;

/* ── Theme management (centralized — Admin page reads from here too) ── */
const THEME_KEY = 'theme';
const getTheme = () => {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') || 'light';
};
const setTheme = (mode) => {
  document.documentElement.setAttribute('data-theme', mode);
  try { localStorage.setItem(THEME_KEY, mode); } catch (_) {}
  // Notify same-tab listeners (the 'storage' event only fires cross-tab)
  window.dispatchEvent(new CustomEvent('themechange', { detail: mode }));
};

/* ── Nav definitions ─────────────────────────────────────────────── */
const NAV_ITEMS = [
  { to: '/',         label: 'Dashboard',     Icon: IconHome,     end: true,  iconOnly: true  },
  { to: '/quotes',   label: 'Quotes',        Icon: IconFileText, end: false },
  { to: '/admin',    label: 'Config',        Icon: IconSliders,  end: false },
  { to: '/users',    label: 'Users',         Icon: IconUser,     end: false, roles: ['system_admin'] },
  { to: '/projects', label: 'Projects',      Icon: IconFolder,   end: false },
];

const Header = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [theme, setThemeState] = useState(getTheme);
  const drawerBtnRef = useRef(null);
  const drawerCloseBtnRef = useRef(null);

  /* Theme cross-component sync (storage event + custom event) */
  useEffect(() => {
    const sync = () => setThemeState(getTheme());
    window.addEventListener('storage', sync);
    window.addEventListener('themechange', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('themechange', sync);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme]);

  /* Projects load */
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await api.getMyProjects();
        setProjects(data || []);
        const saved = localStorage.getItem('selectedProjectId');
        if (saved) {
          setSelectedProjectId(parseInt(saved, 10));
        } else if (data && data.length > 0) {
          setSelectedProjectId(data[0].id);
          localStorage.setItem('selectedProjectId', data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
        // Don't `alert()` — surface inline via console; the page still works
      } finally {
        setInitialized(true);
      }
    };
    loadData();
  }, []);

  /* Drawer: close on route change, Esc key, and focus first close button on open */
  useEffect(() => {
    if (drawerOpen) {
      drawerCloseBtnRef.current?.focus();
      const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = '';
      };
    }
  }, [drawerOpen]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleProjectChange = (projectId) => {
    const newId = projectId ? parseInt(projectId, 10) : null;
    setSelectedProjectId(newId);
    try { localStorage.setItem('selectedProjectId', newId || ''); } catch (_) {}
    window.location.reload();
  };

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const visibleNav = NAV_ITEMS.filter((item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  });

  /* Don't render until initialized to prevent flash of missing project chip */
  if (!initialized) {
    return (
      <>
        <div className="brand-strip" aria-hidden="true" />
        <header className="header-rw" aria-label="ReceiptWise header">
          <div className="header-rw-inner">
            <Link to="/" className="header-rw-logo" aria-label="ReceiptWise home">
              <img src="/receiptwise-logo.jpg" alt="ReceiptWise" />
            </Link>
            <div className="header-rw-spacer" />
          </div>
        </header>
      </>
    );
  }

  return (
    <>
      <div className="brand-strip" aria-hidden="true" />
      <header className="header-rw" role="banner">
        <div className="header-rw-inner">

          {/* Logo */}
          <Link to="/" className="header-rw-logo" aria-label="ReceiptWise home">
            <img src="/receiptwise-logo.jpg" alt="ReceiptWise — Smart Receipt Tracker" />
          </Link>

          {/* Project selector pill (desktop only — collapses to drawer on mobile) */}
          {projects.length > 0 && (
            <label className="header-rw-project" title="Select project">
              <IconFolder size={16} aria-hidden="true" />
              <select
                value={selectedProjectId || ''}
                onChange={(e) => handleProjectChange(e.target.value)}
                aria-label="Project"
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
          )}

          <div className="header-rw-spacer" />

          {/* Desktop nav (lg+) */}
          <nav className="header-rw-nav header-rw-nav-desktop" aria-label="Primary">
            {visibleNav.map(({ to, label, Icon: NavIcon, end, iconOnly }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                title={iconOnly ? label : undefined}
                aria-label={iconOnly ? label : undefined}
                className={({ isActive }) =>
                  `header-rw-link${isActive ? ' is-active' : ''}${iconOnly ? ' header-rw-link--icon-only' : ''}`
                }
              >
                <NavIcon size={16} aria-hidden="true" />
                {!iconOnly && <span>{label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* User chip + logout (desktop) */}
          <div className="header-rw-user-desktop" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {user && (
              <span className="header-rw-user" title={`Signed in as @${user.username}`}>
                <span className="header-rw-user-dot" aria-hidden="true" />
                @{user.username}
              </span>
            )}
            {user && (
              <a
                href={`/u/${user.username}`}
                className="header-rw-icon-btn"
                title="Public profile"
                aria-label="Public profile"
              >
                <IconExternal size={16} />
              </a>
            )}
            <button
              type="button"
              className="header-rw-icon-btn"
              onClick={toggleTheme}
              title={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
              aria-label="Toggle theme"
            >
              <IconSun  size={18} className="header-rw-icon-sun"  />
              <IconMoon size={18} className="header-rw-icon-moon" />
            </button>
            <button onClick={logout} className="header-rw-logout" title="Sign out">
              <IconLogOut size={14} />
              <span>Logout</span>
            </button>
          </div>

          {/* Hamburger (mobile / <lg) */}
          <button
            ref={drawerBtnRef}
            type="button"
            className="header-rw-icon-btn header-rw-hamburger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="rw-drawer"
            style={{ display: 'none' }}
          >
            <IconMenu size={20} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        id="rw-drawer"
        className={`header-rw-drawer${drawerOpen ? ' is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        aria-hidden={!drawerOpen}
      >
        <div className="header-rw-drawer-backdrop" onClick={closeDrawer} aria-hidden="true" />
        <nav className="header-rw-drawer-panel" aria-label="Mobile menu">
          <div className="header-rw-drawer-header">
            <span>Menu</span>
            <button
              ref={drawerCloseBtnRef}
              type="button"
              className="header-rw-icon-btn"
              onClick={closeDrawer}
              aria-label="Close menu"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="header-rw-drawer-body">
            <p className="header-rw-drawer-section-label">Navigation</p>
            {visibleNav.map(({ to, label, Icon: NavIcon, end, iconOnly }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                title={iconOnly ? label : undefined}
                aria-label={iconOnly ? label : undefined}
                className={({ isActive }) =>
                  `header-rw-drawer-link${isActive ? ' is-active' : ''}${iconOnly ? ' header-rw-drawer-link--icon-only' : ''}`
                }
              >
                <NavIcon size={18} aria-hidden="true" />
                {!iconOnly && <span>{label}</span>}
              </NavLink>
            ))}

            <p className="header-rw-drawer-section-label">Account</p>
            {user && (
              <a
                href={`/u/${user.username}`}
                className="header-rw-drawer-link"
                onClick={closeDrawer}
              >
                <IconExternal size={18} />
                <span>Public profile</span>
              </a>
            )}
            <button
              type="button"
              className="header-rw-drawer-link"
              onClick={() => { toggleTheme(); closeDrawer(); }}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit' }}
            >
              <IconMoon size={18} className="header-rw-icon-moon" aria-hidden="true" />
              <IconSun  size={18} className="header-rw-icon-sun"  aria-hidden="true" />
              <span>{theme === 'light' ? 'Dark theme' : 'Light theme'}</span>
            </button>
            {user && (
              <button
                type="button"
                className="header-rw-drawer-link"
                onClick={() => { closeDrawer(); logout(); }}
                style={{ background: 'transparent', border: 0, cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit', color: 'rgb(var(--c-danger))' }}
              >
                <IconLogOut size={18} />
                <span>Logout</span>
              </button>
            )}
          </div>

          {user && (
            <div className="header-rw-drawer-user">
              <span className="header-rw-drawer-user-name">@{user.username}</span>
              <span className="header-rw-user-dot" aria-hidden="true" />
            </div>
          )}
        </nav>
      </div>
    </>
  );
};

export default Header;
export { getTheme, setTheme };